import { useEffect, useMemo, useState } from 'react';
import TileCard from './TileCard';
import { getTracesByCorrelation, getWorkflowActionPayload } from '../api/client';
import type { Env, TraceEvent, TracesByCorrelation, WorkflowActionPayload } from '../types';

const HELP =
  'Paste a correlation ID (clientTrackingId from a failed Logic App run) to see ' +
  'the journey grouped by workflow. Click any row to see what was sent and received. ' +
  'Internal plumbing actions (Initialize_Variables, Compose, Set, etc.) are hidden by ' +
  'default — toggle "Show all" to see the full action sequence.';

interface Props {
  env: Env;
  initialCorrelationId?: string;
  windowHours?: number;
  refreshToken?: number;
}

const SHOW_ALL_STORAGE_KEY = 'synergy.trace.showAll';

const SAFE_ID = /^[A-Za-z0-9._:\-]{1,128}$/;

// Heuristic — which action names carry meaningful data the user actually
// wants to see in Simple view. Conservative: anything starting with these
// prefixes makes an external/SDK call or returns data; everything else is
// internal plumbing (Compose, Initialize_Variables, Set_*, control-flow).
//
// Triggers (where actionName is empty) and run-level rows are always shown
// because they're the "what arrived" / "what was returned" hooks for the
// whole journey.
const MEANINGFUL_ACTION_PREFIXES = [
  'HTTP_',
  'Call_',
  'Notify_',
  'SQL_',
  'Semarchy_',
  'Auth0_',
  'SF_',
  'Send_',
  'Response_',
  'Create_',
  'Delete_',
  'Update_',
  'Patch_',
  'Query_',
  'Get_',
];

// Only events from syn-* workflows are interesting in Simple view. The LA
// host's own Application-Insights-emitted entries (AppRoleName
// "logicapps-ccc-t-uks-01") are infrastructure plumbing and were polluting
// the view as a "logicapps-..." group.
function isSynergyEvent(event: TraceEvent): boolean {
  return event.workflowName.startsWith('syn-');
}

function isMeaningful(event: TraceEvent): boolean {
  // Drop non-syn-* workflows entirely (LA host's own AppRequests etc.)
  if (!isSynergyEvent(event)) return false;
  // Trigger / run-level events
  if (!event.actionName) return true;
  // External calls + response actions
  if (MEANINGFUL_ACTION_PREFIXES.some((p) => event.actionName.startsWith(p))) return true;
  // Exception or known failure always meaningful
  if (event.kind === 'Exception' || event.success === false) return true;
  return false;
}

// Some actions never have captured inputs/outputs (InitializeVariable,
// SetVariable, Compose, control flow). Showing the chevron just leads users
// to expand to a "no data captured" message — confusing. Restrict the chevron
// to actions that we know carry data (HTTP, Service Provider, Response) plus
// trigger / run-level events.
const DATA_BEARING_PREFIXES = [
  'HTTP_',
  'Call_',
  'Notify_',
  'SQL_',
  'Semarchy_',
  'Auth0_',
  'SF_',
  'Send_',
  'Response_',
  'Create_',
  'Delete_',
  'Update_',
  'Patch_',
  'Query_',
  'Get_',
];

function hasPayloadData(event: TraceEvent): boolean {
  // Triggers always have content (TriggerInputs/TriggerOutputs)
  if (!event.actionName) return true;
  return DATA_BEARING_PREFIXES.some((p) => event.actionName.startsWith(p));
}

function formatDuration(ms: number | null): string {
  if (ms === null) return '';
  if (ms < 1000) return `${ms} ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)} s`;
  return `${(ms / 60_000).toFixed(1)} min`;
}

// Friendly status icon — ✓ green / ✗ red / ⏳ grey
function statusIcon(success: boolean | null): { sym: string; cls: string } {
  if (success === false) return { sym: '✗', cls: 'trace-status-fail' };
  if (success === true) return { sym: '✓', cls: 'trace-status-ok' };
  return { sym: '·', cls: 'trace-status-pending' };
}

// An envelope icon flags the workflow that actually sends the email —
// syn-shared-send-email. That's the only signal we trust; trying to infer
// from action names is too lossy.
function firedEmail(e: TraceEvent): boolean {
  return e.workflowName === 'syn-shared-send-email';
}

function workflowFiredEmail(events: TraceEvent[]): boolean {
  return events.some(firedEmail);
}

// Overall workflow status — fails if any event in the group failed.
function workflowStatus(events: TraceEvent[]): boolean | null {
  let sawSuccess = false;
  for (const e of events) {
    if (e.success === false || e.kind === 'Exception') return false;
    if (e.success === true) sawSuccess = true;
  }
  return sawSuccess ? true : null;
}

// LogicAppWorkflowRuntime emits a Started + Completed pair for every action
// and trigger lifecycle event. Started carries Status='Running' (no
// resultCode, success=null); Completed carries the final Status. This
// reducer collapses both into one row per (runId, actionName) by keeping
// the LATEST event by timestamp — which is always the Completed row, since
// Started fires first. The trigger lifecycle trio (RunStarted +
// TriggerStarted + TriggerCompleted) is collapsed to a single "Trigger" row
// per workflow run.
function collapseTriggerEvents(events: TraceEvent[]): TraceEvent[] {
  const triggerSeen = new Set<string>();
  const bestPerKey = new Map<string, TraceEvent>();
  const passthrough: TraceEvent[] = [];

  for (const e of events) {
    const isTrigger =
      !e.actionName &&
      e.runId &&
      (e.name.startsWith('WorkflowRun') || e.name.startsWith('WorkflowTrigger'));
    if (isTrigger) {
      const k = `trig:${e.runId}:${e.workflowName}`;
      if (triggerSeen.has(k)) continue;
      triggerSeen.add(k);
      passthrough.push({ ...e, name: 'Trigger' });
      continue;
    }
    if (e.runId && e.actionName) {
      // Any LA-emitted action row — dedupe by (runId, actionName) keeping
      // the latest by timestamp. Started comes first, Completed later, so
      // the latest always wins and we end up with the final state.
      const k = `${e.runId}:${e.actionName}`;
      const prev = bestPerKey.get(k);
      if (!prev || prev.timestamp < e.timestamp) {
        bestPerKey.set(k, e);
      }
      continue;
    }
    passthrough.push(e);
  }
  const out = [...passthrough, ...bestPerKey.values()];
  out.sort((a, b) => a.timestamp.localeCompare(b.timestamp));
  return out;
}

// Stable row identity for expansion state + payload cache.
function rowKey(e: TraceEvent, idx: number): string {
  if (e.runId) {
    return `la:${e.runId}:${e.actionName || 'trigger'}`;
  }
  return `evt:${e.timestamp}:${e.name}:${idx}`;
}

function isExpandable(e: TraceEvent): boolean {
  // Must be an LA-emitted row (has runId) AND of a type we expect to have
  // captured payloads. Compose / InitializeVariable / SetVariable / control
  // flow never have I/O, so showing the chevron just frustrates the user.
  return Boolean(e.runId) && hasPayloadData(e);
}

function groupByWorkflow(events: TraceEvent[]): Array<{ workflowName: string; events: TraceEvent[] }> {
  const order: string[] = [];
  const groups = new Map<string, TraceEvent[]>();
  for (const e of events) {
    const key = e.workflowName || '(no workflow)';
    if (!groups.has(key)) {
      groups.set(key, []);
      order.push(key);
    }
    groups.get(key)!.push(e);
  }
  return order.map((k) => ({ workflowName: k, events: groups.get(k)! }));
}

function loadShowAll(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(SHOW_ALL_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

function saveShowAll(v: boolean): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(SHOW_ALL_STORAGE_KEY, v ? '1' : '0');
  } catch {
    /* ignore */
  }
}

interface PayloadState {
  loading: boolean;
  data?: WorkflowActionPayload;
  error?: string;
}

export default function CorrelationTracePanel({
  env,
  initialCorrelationId = '',
  windowHours = 168,
  refreshToken = 0,
}: Props) {
  const [input, setInput] = useState(initialCorrelationId);
  const [submitted, setSubmitted] = useState(initialCorrelationId);
  const [data, setData] = useState<TracesByCorrelation | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAll, setShowAll] = useState<boolean>(loadShowAll);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [payloads, setPayloads] = useState<Map<string, PayloadState>>(new Map());

  useEffect(() => {
    if (initialCorrelationId && initialCorrelationId !== submitted) {
      setInput(initialCorrelationId);
      setSubmitted(initialCorrelationId);
    }
  }, [initialCorrelationId]);

  useEffect(() => {
    if (!submitted) {
      setData(null);
      setError(null);
      setExpanded(new Set());
      setPayloads(new Map());
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    setExpanded(new Set());
    setPayloads(new Map());
    getTracesByCorrelation(env, submitted, windowHours)
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((e: Error) => {
        if (!cancelled) setError(e.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [env, submitted, windowHours, refreshToken]);

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = input.trim();
    if (!SAFE_ID.test(trimmed)) {
      setError('Invalid correlation ID. Must be 1-128 chars of [A-Za-z0-9._:-].');
      return;
    }
    setError(null);
    setSubmitted(trimmed);
  }

  function toggleShowAll() {
    const v = !showAll;
    setShowAll(v);
    saveShowAll(v);
  }

  function toggleExpand(key: string, event: TraceEvent) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
    if (!payloads.has(key) && isExpandable(event)) {
      setPayloads((prev) => new Map(prev).set(key, { loading: true }));
      // For trigger rows actionName is empty; the backend then fetches TriggerInputs/TriggerOutputs.
      getWorkflowActionPayload(env, event.workflowName, event.runId, event.actionName || '')
        .then((p) =>
          setPayloads((prev) => new Map(prev).set(key, { loading: false, data: p })),
        )
        .catch((err: Error) =>
          setPayloads((prev) => new Map(prev).set(key, { loading: false, error: err.message })),
        );
    }
  }

  const allEvents: TraceEvent[] = data?.events ?? [];
  // syn-* filter is ALWAYS applied — even with "Show all actions" enabled we
  // never want the LA host's own logicapps-* AppInsights noise. "Show all"
  // only re-enables the plumbing actions (Initialize_Variables, Set_*,
  // Compose_*) inside syn-* workflows.
  const synOnlyEvents = useMemo(() => allEvents.filter(isSynergyEvent), [allEvents]);
  const visibleEvents = useMemo(() => {
    const collapsed = collapseTriggerEvents(synOnlyEvents);
    return showAll ? collapsed : collapsed.filter(isMeaningful);
  }, [synOnlyEvents, showAll]);
  const groups = useMemo(() => groupByWorkflow(visibleEvents), [visibleEvents]);
  const hiddenCount = synOnlyEvents.length - visibleEvents.length;

  // Failure summary — first failed event in the chain (most-recent run that
  // had a Failed action). Surface as a banner at the top so the user sees
  // the root cause without scrolling.
  const firstFailure = useMemo(
    () => synOnlyEvents.find((e) => e.success === false || e.kind === 'Exception'),
    [synOnlyEvents],
  );
  const failureBanner = firstFailure ? (
    <div className="trace-failure-banner" role="alert">
      <span className="trace-status-pill trace-status-fail">✗</span>
      <span className="trace-failure-text">
        <strong>{firstFailure.actionName || firstFailure.name || 'Action failed'}</strong>
        {firstFailure.resultCode && <span className="muted"> · {firstFailure.resultCode}</span>}
        {firstFailure.workflowName && <span className="muted"> · in {firstFailure.workflowName}</span>}
        {firstFailure.message && <div className="trace-failure-msg">{firstFailure.message}</div>}
      </span>
    </div>
  ) : null;

  const footer = data ? (
    <span>
      As of {new Date(data.generatedAt).toLocaleTimeString()} · {windowHours}h search window
      {!showAll && hiddenCount > 0 && ` · ${hiddenCount} plumbing actions hidden`}
      {data.truncated && ' · truncated to 500 events'}
    </span>
  ) : undefined;

  return (
    <TileCard
      title={`Correlation trace — ${env.toUpperCase()}`}
      help={HELP}
      loading={loading}
      error={error}
      footer={footer}
    >
      <form className="trace-form" onSubmit={onSubmit}>
        <label className="wfrun-label">
          Correlation ID
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="e.g. dafbf0be-be42-436d-b777-05e6a9d7f4ef"
            className="trace-input"
            spellCheck={false}
            autoComplete="off"
            aria-label="Correlation ID"
          />
        </label>
        <button type="submit" className="dlq-resubmit-btn" disabled={!input.trim()}>
          Look up
        </button>
        {submitted && (
          <label className="wfrun-label trace-showall">
            <input
              type="checkbox"
              checked={showAll}
              onChange={toggleShowAll}
              aria-label="Show all actions"
            />
            <span>Show all actions</span>
          </label>
        )}
      </form>

      {submitted && failureBanner}

      {!submitted && (
        <p className="tile-empty">
          Enter a correlation ID above, or open this panel from a failed run on the Workflows
          detail page.
        </p>
      )}
      {submitted && data?.status === 'not-configured' && <p className="tile-empty">{data.message}</p>}
      {submitted && data?.status === 'error' && <div className="tile-error">Query failed: {data.message}</div>}
      {submitted && data?.status === 'ok' && allEvents.length === 0 && (
        <p className="tile-empty">
          No events found for <code>{submitted}</code> in the last {windowHours}h.
        </p>
      )}
      {submitted && data?.status === 'ok' && visibleEvents.length === 0 && allEvents.length > 0 && (
        <p className="tile-empty">
          {allEvents.length} events found but all are internal plumbing. Toggle{' '}
          <strong>Show all actions</strong> above to see them.
        </p>
      )}

      {submitted && data?.status === 'ok' && visibleEvents.length > 0 && (
        <SimpleView
          groups={groups}
          expanded={expanded}
          payloads={payloads}
          onToggle={toggleExpand}
        />
      )}
    </TileCard>
  );
}

interface ViewProps {
  expanded: Set<string>;
  payloads: Map<string, PayloadState>;
  onToggle: (key: string, e: TraceEvent) => void;
}

function SimpleView({
  groups,
  expanded,
  payloads,
  onToggle,
}: ViewProps & { groups: Array<{ workflowName: string; events: TraceEvent[] }> }) {
  return (
    <div className="trace-simple">
      {groups.map((g) => {
        const wfStatus = statusIcon(workflowStatus(g.events));
        const wfEmail = workflowFiredEmail(g.events);
        return (
          <section key={g.workflowName} className="trace-simple-group">
            <h4 className="trace-simple-wfname">
              {g.workflowName}
              <span className="trace-wf-summary">
                {wfEmail && <span className="trace-email-icon" title="Email triggered in this workflow">✉️</span>}
                <span className={`trace-status-pill ${wfStatus.cls}`} title="Overall workflow status">
                  {wfStatus.sym}
                </span>
              </span>
            </h4>
            <ul className="trace-simple-list">
              {g.events.map((e, idx) => {
                const key = rowKey(e, idx);
                const isOpen = expanded.has(key);
                const can = isExpandable(e);
                const st = statusIcon(e.success);
                const email = firedEmail(e);
                return (
                  <li key={key} className={`trace-simple-row ${can ? 'trace-row-expandable' : ''}`}>
                    <button
                      type="button"
                      className="trace-simple-toggle"
                      onClick={() => can && onToggle(key, e)}
                      disabled={!can}
                      aria-expanded={can ? isOpen : undefined}
                      title={
                        can
                          ? isOpen
                            ? 'Hide sent/received data'
                            : 'Show sent/received data'
                          : 'No payload available'
                      }
                    >
                      <span className="trace-chevron">{can ? (isOpen ? '▼' : '▶') : ''}</span>
                      <span className="trace-simple-name">{e.actionName || e.name || '(unnamed)'}</span>
                      {e.target && e.target !== e.workflowName && (
                        <span className="trace-simple-target muted">→ {e.target}</span>
                      )}
                      {e.resultCode && (
                        <span className="trace-simple-code muted">{e.resultCode}</span>
                      )}
                      {e.durationMs !== null && (
                        <span className="trace-simple-duration muted">{formatDuration(e.durationMs)}</span>
                      )}
                      {email && <span className="trace-email-icon" title="Triggered an email">✉️</span>}
                      <span className={`trace-status-pill ${st.cls}`}>{st.sym}</span>
                    </button>
                    {isOpen && can && <PayloadView state={payloads.get(key)} />}
                  </li>
                );
              })}
            </ul>
          </section>
        );
      })}
    </div>
  );
}

function PayloadView({ state }: { state?: PayloadState }) {
  if (!state || state.loading) return <div className="trace-payload-loading">Loading…</div>;
  if (state.error) return <div className="tile-error">Payload fetch failed: {state.error}</div>;
  const p = state.data;
  if (!p) return null;
  if (p.status === 'not-configured') return <p className="tile-empty">{p.message}</p>;
  if (p.status === 'not-found') return <p className="tile-empty">{p.message}</p>;
  if (p.status === 'error') return <div className="tile-error">{p.message}</div>;
  return (
    <div className="trace-payload">
      <div className="trace-payload-panel">
        <div className="trace-payload-header">
          <h5 className="trace-payload-heading">Sent</h5>
          {p.inputsAvailable && (
            <button
              type="button"
              className="trace-payload-copy"
              onClick={() => copyJsonToClipboard(p.inputs)}
              title="Copy JSON to clipboard"
            >
              Copy
            </button>
          )}
        </div>
        {p.inputsAvailable ? (
          <JsonView value={p.inputs} />
        ) : (
          <p className="trace-payload-empty">{p.message || 'No inputs captured.'}</p>
        )}
      </div>
      <div className="trace-payload-panel">
        <div className="trace-payload-header">
          <h5 className="trace-payload-heading">Received</h5>
          {p.outputsAvailable && (
            <button
              type="button"
              className="trace-payload-copy"
              onClick={() => copyJsonToClipboard(p.outputs)}
              title="Copy JSON to clipboard"
            >
              Copy
            </button>
          )}
        </div>
        {p.outputsAvailable ? (
          <JsonView value={p.outputs} />
        ) : (
          <p className="trace-payload-empty">{p.message || 'No outputs captured.'}</p>
        )}
      </div>
    </div>
  );
}

function copyJsonToClipboard(value: unknown): void {
  try {
    void navigator.clipboard.writeText(JSON.stringify(value, null, 2));
  } catch {
    /* ignore */
  }
}

// Minimal JSON viewer with syntax highlighting via CSS classes. Works in a
// scrollable <pre> so large payloads don't break the layout. Avoids pulling
// in a 3rd-party JSON-tree dependency.
function JsonView({ value }: { value: unknown }) {
  const html = useMemo(() => highlightJson(value), [value]);
  return (
    <pre
      className="trace-payload-json"
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

function highlightJson(value: unknown): string {
  let json: string;
  try {
    json = JSON.stringify(value, null, 2);
  } catch {
    return escapeHtml(String(value));
  }
  if (json === undefined) return '';
  return escapeHtml(json).replace(
    /(&quot;[^&]*?&quot;)(\s*:)?|(\b-?\d+(?:\.\d+)?\b)|\b(true|false|null)\b/g,
    (match, str, colon, num, lit) => {
      if (str) {
        if (colon) return `<span class="jk">${str}</span>${colon}`;
        return `<span class="js">${str}</span>`;
      }
      if (num) return `<span class="jn">${num}</span>`;
      if (lit) return `<span class="jl">${lit}</span>`;
      return match;
    },
  );
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
